import React, { useEffect, useRef, useState, useCallback, lazy, Suspense } from 'react'
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'

import Layout from '@components/Layout/Layout'
import AppErrorBoundary from '@components/ErrorBoundary/AppErrorBoundary'

// 懒加载非关键路由（减小首屏 bundle 体积）
const NotesPage = lazy(() => import('@components/Pages/NotesPage'))
const NotesPageWithSync = lazy(() => import('@components/Pages/NotesPageWithSync'))
const SettingsPage = lazy(() => import('@components/Pages/SettingsPage'))
const NotFoundPage = lazy(() => import('@components/Pages/NotFoundPage'))
const TauriTestPage = lazy(() => import('./pages/TauriTest'))
const TipTapMobileTestPage = lazy(() => import('./pages/TipTapMobileTestPage'))
const PlanSubscriptionPage = lazy(() =>
  import('@components/Plans').then(m => ({ default: m.PlanSubscriptionPage }))
)

// 模式特定的编辑器容器（标准模式为死代码路由，懒加载）
const MultiNoteEditorContainer = lazy(() => import('@components/MultiNoteEditor/MultiNoteEditorContainer'))

// 速记/CRDT 双模式保活宿主（内部按模式懒加载两个页面组件；chunk 预热见 main.tsx）
const EditorModeHost = lazy(() => import('@components/Pages/EditorModeHost'))

// 新的认证模块
import { LoginPage, CallbackPage, EmailBindingPage } from './auth/pages'
import { ProtectedRoute } from './auth/components'
import { useAuthStore } from './auth/store'
import { useOAuthCallbackListener, useOAuthUrlCallback, useRestoreSession } from './auth/hooks'
import { ResetPasswordModal } from '@components/LockNote'

// Context
import { EditorProvider } from '@contexts/EditorContext'

import { setOnlineStatus } from '@store/slices/syncSlice'
import { setTheme, setSidebarOpen, setTocOutlineOpen, setBlockReferencePanelOpen } from '@store/slices/uiSlice'
import { closeRightEditor, closeAllNotesInRightEditor } from '@store/slices/rightEditorSlice'
import { closeLeftEditorOnly } from '@store/slices/leftEditorSlice'
import { RootState } from '@store'

import { tauriApi } from './api/tauriApi'
import { setupMobileViewport, setupMobileSafeArea, watchStatusBarAppearance, isTauriMobilePlatform, isWebMobileBrowser } from './utils/mobile'
import { useMobile } from './hooks/useMobile'
import { ElectricSyncProvider, useElectricSyncContext } from './components/ElectricSyncProvider'
import { SyncStatusProvider } from './contexts/SyncStatusContext'
import ReadonlyModeBanner from '@components/ReadonlyModeBanner'
// 慢启动提示弹窗（启动 >8s 时延迟弹出；未触发时渲染 null，零成本）
import StartupSlowHintDialog from '@components/common/StartupSlowHintDialog'
import { onElectricStatusChange, checkElectricAvailableWithRetry } from './store/collections'
import { useAutoReadonlyFallback } from './hooks/useAutoReadonlyFallback'
import { historyService } from './services/HistoryService'

// 版本管理：自动检查更新与更新提示 Modal
import { useUpdateChecker } from './hooks/useUpdateChecker'
// 鸿蒙分享接收：系统分享面板 → 深链 app://share → 建笔记并打开
import { useHarmonyShareReceive } from './hooks/useHarmonyShareReceive'
// 鸿蒙应用锁（B7）：生物识别/锁屏密码解锁门
import HarmonyAppLockGate from '@components/HarmonyAppLockGate'
// 懒加载更新弹窗：连带 antd 组件移出首屏 bundle，检测到更新时才加载
const UpdateModal = lazy(() => import('@components/UpdateModal/UpdateModal'))

import './styles/globals.css'
// TipTap UI Components 样式
import '@/styles/_variables.scss'
import '@/styles/_keyframe-animations.scss'

const ElectricSyncStatusLogger: React.FC = () => {
  const { isInitialized, isSyncing, lastSyncAt, error } = useElectricSyncContext()
  const { isAuthenticated, user } = useAuthStore()

  useEffect(() => {
    console.log('[App] Electric SQL 同步状态:', {
      isAuthenticated,
      userId: user?.id,
      isInitialized,
      isSyncing,
      lastSyncAt,
      error: error?.message,
    })
  }, [isAuthenticated, user?.id, isInitialized, isSyncing, lastSyncAt, error])

  return null
}

const StartupRedirect: React.FC = () => {
  const lastEditorMode = localStorage.getItem('last_editor_mode')
  const autoSuji = localStorage.getItem('auto_suji_mode_startup') === 'true'
  const lastEditedNoteId = localStorage.getItem('last_edited_note_id')

  // 优先级：last_editor_mode=suji > auto_suji_mode_startup > last_editor_mode=crdt > 兜底 /suji
  // 1. 速记模式（last_editor_mode=suji 或 auto_suji_mode_startup=true）
  if (lastEditorMode === 'suji' || autoSuji) {
    return <Navigate to="/suji" replace />
  }
  // 2. CRDT 模式且有最近编辑的笔记
  if (lastEditorMode === 'crdt' && lastEditedNoteId) {
    return <Navigate to={`/crdt/notes/${lastEditedNoteId}`} replace />
  }
  // 3. 兜底速记模式
  return <Navigate to="/suji" replace />
}

// 懒加载路由的加载占位（与 SujiLoadingSkeleton 视觉一致，本地 chunk 加载极快）
const RouteLoadingFallback: React.FC = () => (
  <div className="h-full flex flex-col items-center justify-center bg-white dark:bg-gray-900">
    <div className="w-10 h-10 border-[3px] border-gray-200 dark:border-gray-700 border-t-blue-500 rounded-full animate-spin" />
  </div>
)

// ★ 模式轨道统一入口：/notes（全部笔记页）与 suji/crdt 四条路由渲染同一元素类型
// （移动端均为 <Suspense><EditorModeHost/></Suspense>），保证 React 跨路由不重挂载：
// - 修复"全部笔记页 → 速记"轨道切换卡顿（原 /notes 走 NotesRouteEntry，元素类型与
//   其它路由不同 → EditorModeHost 整树重挂载 → 速记页重新全量初始化阻塞主线程）
// - 修复"关闭全部笔记后再从速记左滑"时，/notes 重挂载触发会话恢复重开已关闭笔记
// 桌面端保持原行为：/notes 渲染独立 NotesPage，其余路由渲染 EditorModeHost。
const NotesRouteEntry: React.FC = () => {
  const location = useLocation()
  const isDesktopNotes = !isTauriMobilePlatform() && (location.pathname === '/notes' || location.pathname.startsWith('/notes/'))
  return (
    <Suspense fallback={<RouteLoadingFallback />}>
      {isDesktopNotes ? <NotesPage /> : <EditorModeHost />}
    </Suspense>
  )
}

function App() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { theme } = useSelector((state: RootState) => state.ui)
  const { isAuthenticated, expiresAt, accessToken, user } = useAuthStore()
  const hasCheckedToken = useRef(false)
  const [hasSyncedUser, setHasSyncedUser] = useState(false)

  // ★ 离线/不稳定自动只读（统一策略：启动期 + 会话中途，详见 useAutoReadonlyFallback）
  useAutoReadonlyFallback()

  // 应用更新检查（启动 1 分钟后自动检查，每次启动都检查，不做 24h 频率限制）
  // App.tsx 仅负责自动检查与 Modal 显示；手动检查入口在 SettingsPage / UserProfile
  // 静默策略：非强制更新走"自动检查 → 静默后台下载 → Header 圆圈按钮提示"流程，不弹 Modal
  const {
    updateInfo: updateInfoForModal,
    isChecking: isUpdateChecking,
    isUpdating: isApplyingUpdate,
    error: updateError,
    progress: updateProgress,
    downloadStatus: updateDownloadStatus,
    dismiss: dismissUpdate,
    applyUpdate: applyAppUpdate,
    startDownload: startUpdateDownload,
    installDownloaded: installDownloadedUpdate,
    checkNow: checkUpdateNow,
  } = useUpdateChecker({
    checkOnMount: true,
    delayMs: 60000,
    skipIfCheckedRecently: false,
  })

  // 移动端检测：必须定义在所有引用 isMobile 的 useEffect 之前，否则 TDZ 错误
  const mobileInfo = useMobile()
  const isMobile = mobileInfo.isTauriMobile
  const mobileInitedRef = useRef(false)
  // 状态栏外观监听清理句柄（watchStatusBarAppearance 返回，卸载时调用）
  const unwatchStatusBarRef = useRef<(() => void) | null>(null)

  // 启动时自动更新 Modal 的显示状态（与 updateInfo 解耦，避免关闭 Modal 时被迫 dismiss）
  const [appUpdateModalOpen, setAppUpdateModalOpen] = useState(false)
  // "检测到新版本"阶段就弹的场景（目前仅 forceUpdate）—— 同一版本只弹一次
  const lastShownVersionRef = useRef<string | null>(null)
  // "下载完成后"阶段才弹的场景（移动端普通更新）—— 同一版本只弹一次，与上面 ref 独立跟踪
  const lastShownAfterDownloadRef = useRef<string | null>(null)

  // 当自动检查检测到新版本时，决定是否立即弹 Modal（检测到新版本阶段）
  // 桌面端：仅强制更新（forceUpdate=true）立即弹 Modal
  // 移动端：普通更新不立即弹（改为直接静默后台下载，下载完成后再弹）；仅强制更新立即弹
  // 已忽略版本（dismissed=true）不自动弹窗，但 updateInfo 仍保留供用户手动触发检查使用
  // 手动检查（Settings/UserProfile）有各自独立的 Modal，不受此逻辑影响
  useEffect(() => {
    if (updateInfoForModal?.hasUpdate && updateInfoForModal?.version) {
      // 仅强制更新才在"检测到新版本"阶段立即弹 Modal
      const shouldShowModalOnDetect = updateInfoForModal.forceUpdate && !updateInfoForModal.dismissed
      if (shouldShowModalOnDetect && lastShownVersionRef.current !== updateInfoForModal.version) {
        lastShownVersionRef.current = updateInfoForModal.version
        setAppUpdateModalOpen(true)
      }
    } else if (!updateInfoForModal) {
      // updateInfo 被清空时重置两个阶段的记录，下次检测到新版本时再正常触发
      lastShownVersionRef.current = null
      lastShownAfterDownloadRef.current = null
    }
  }, [updateInfoForModal, isMobile])

  // 移动端普通更新：后台静默下载完成后再弹 Modal 提示用户点击安装
  // - 仅移动端
  // - downloadStatus === 'downloaded'（真实完成 / localStorage 恢复都能触发）
  // - 非 dismissed 版本（用户明确忽略的版本不下载也不弹）
  // - 同一版本只弹一次（lastShownAfterDownloadRef 守卫，避免前台/后台切换重复弹）
  useEffect(() => {
    if (!isMobile) return
    if (updateDownloadStatus !== 'downloaded') return
    if (!updateInfoForModal?.hasUpdate || updateInfoForModal.dismissed) return

    const version = updateInfoForModal.version
    if (!version) return
    if (lastShownAfterDownloadRef.current === version) return

    console.log('[App] 移动端静默下载完成，弹出更新安装提示: version=', version)
    lastShownAfterDownloadRef.current = version
    setAppUpdateModalOpen(true)
  }, [updateDownloadStatus, updateInfoForModal, isMobile])

  // 恢复会话（应用启动时检查是否已登录）
  const { isRestoring } = useRestoreSession()

  // 乐观放行后 restoreSession 失败的跳转
  // ProtectedRoute 有本地缓存时乐观放行，如果 restoreSession 最终失败（token 过期且刷新失败），
  // 需要跳转到 /login 并清除本地缓存标记
  useEffect(() => {
    if (!isRestoring && !isAuthenticated) {
      // 排除 OAuth 回调处理中的情况
      const params = new URLSearchParams(window.location.search)
      const hasOAuthCallback = params.has('code') && params.has('state')
      const isCallbackProcessing = sessionStorage.getItem('oauth_callback_processing') === 'true'
      if (hasOAuthCallback || isCallbackProcessing) return

      // restoreSession 已完成但未认证 → 清除缓存标记
      localStorage.removeItem('modern_notes_user_id')

      // ★ 已在 /login 路由时跳过冗余导航（全平台）：
      // Web 版 LoginPage 可能已发起 Casdoor 顶级导航（window.location.href），
      // 此处再 navigate（hash 变更/replaceState）会取消进行中的导航（net::ERR_ABORTED）。
      // 注意必须读实时 window.location.hash 而非闭包里的 location.pathname ——
      // 本 effect 与 ProtectedRoute 的 navigate('/login') 同属一个 commit，
      // 闭包捕获的是旧路由（如 /suji），读实时值才能看到子组件已完成的 #/login 跳转。
      if (window.location.hash.startsWith('#/login')) return

      navigate('/login?from=session_expired', { replace: true })
    }
  }, [isRestoring, isAuthenticated, navigate, location.pathname])

  // 设置 OAuth 回调监听（用于嵌入式登录窗口）
  useOAuthCallbackListener()

  // 设置 OAuth URL 回调检测（桌面端 window.location.href 导航后返回根路径）
  useOAuthUrlCallback()

  // 鸿蒙分享接收：监听 app://share 深链，建笔记并打开（其它平台前缀不匹配，零开销）
  useHarmonyShareReceive()

  // 启动时清理过期的 OAuth 缓存和处理标记
  useEffect(() => {
    import('./auth/casdoor').then(({ cleanupOAuthState }) => {
      cleanupOAuthState()
    })
  }, [])

  // 延迟调度历史数据库清理（启动完成后空闲时执行，不阻塞启动）
  useEffect(() => {
    historyService.scheduleStartupCleanup()
    return () => {
      historyService.destroy()
    }
  }, [])

  // 忘记密码重置流程状态
  const [showResetModal, setShowResetModal] = useState(false)
  const hasShownResetModalRef = useRef(false)

  // 网络连接状态提示（状态机：hidden → connecting → connected/failed）
  type NetworkStatus = 'hidden' | 'connecting' | 'connected' | 'failed'
  const [networkStatus, setNetworkStatus] = useState<NetworkStatus>('hidden')
  const hasChosenOfflineRef = useRef(false)        // 用户主动选择离线使用后会话内不再提示
  const isConnectingRef = useRef(false)            // 重入保护：防止健康检查失败回调同步触发 startConnecting 形成无限循环
  const retryTokenRef = useRef<{ cancelled: boolean }>({ cancelled: false })
  const autoHideTimerRef = useRef<number | null>(null)

  // 启动 8 秒持续连接尝试
  const startConnecting = useCallback(() => {
    if (hasChosenOfflineRef.current) return  // 会话抑制
    if (isConnectingRef.current) return      // 正在连接中，跳过（防止重入循环）

    isConnectingRef.current = true

    // 取消之前的重试 token
    retryTokenRef.current.cancelled = true
    const newToken = { cancelled: false }
    retryTokenRef.current = newToken

    setNetworkStatus('connecting')

    checkElectricAvailableWithRetry(8000, 2000, newToken).then((ok) => {
      isConnectingRef.current = false  // 重置，允许下次重连
      if (newToken.cancelled) return
      if (ok) {
        setNetworkStatus('connected')
        if (autoHideTimerRef.current) clearTimeout(autoHideTimerRef.current)
        autoHideTimerRef.current = window.setTimeout(() => {
          if (!newToken.cancelled) setNetworkStatus('hidden')
        }, 1500)
      } else {
        setNetworkStatus('failed')
      }
    })
  }, [])

  // 网络恢复处理：仅在 connecting/failed 状态下显示"网络已连接"
  const handleConnected = useCallback(() => {
    retryTokenRef.current.cancelled = true  // 取消正在进行的重试
    isConnectingRef.current = false  // 重置，允许下次重连
    setNetworkStatus((prev) => {
      if (prev === 'hidden' || prev === 'failed') {
        // 静默恢复，不弹 Banner（避免噪声）
        return 'hidden'
      }
      // connecting → connected：显示"网络已连接"成功提示
      if (autoHideTimerRef.current) clearTimeout(autoHideTimerRef.current)
      autoHideTimerRef.current = window.setTimeout(() => {
        setNetworkStatus('hidden')
      }, 1500)
      return 'connected'
    })
  }, [])

  // 监听 Electric 服务状态和网络状态
  useEffect(() => {
    const handleOffline = () => {
      console.log('[App] 网络断开')
      setNetworkStatus('failed')
    }

    const handleOnline = () => {
      console.log('[App] 网络恢复')
      handleConnected()
    }

    window.addEventListener('offline', handleOffline)
    window.addEventListener('online', handleOnline)

    // 不在启动时自动 startConnecting（乐观 UI 最佳实践）
    // 连接状态由 useElectricSync 的 collection.on('status:change') 驱动
    // 仅在用户手动点击"重试"时调用 startConnecting
    const unsubscribe = onElectricStatusChange((available) => {
      if (available) {
        handleConnected()
        // 离线/恢复提示统一由 ReadonlyModeBanner 常驻横幅呈现（四态：
        // 离线只读 / 网络不稳定 / 恢复中 / 退出闪现），此处不再弹 toast 气泡
      }
      // available === false 时不自动 startConnecting，避免循环
      // 连接失败状态由 useElectricSync 的 error/isConnected 驱动
    })

    return () => {
      window.removeEventListener('offline', handleOffline)
      window.removeEventListener('online', handleOnline)
      unsubscribe()
      retryTokenRef.current.cancelled = true
      if (autoHideTimerRef.current) clearTimeout(autoHideTimerRef.current)
    }
  }, [startConnecting, handleConnected])

  // 用户点击"离线使用"：本次会话不再提示
  const handleUseOffline = useCallback(() => {
    console.log('[App] 用户选择离线使用，本次会话不再提示')
    hasChosenOfflineRef.current = true
    retryTokenRef.current.cancelled = true
    setNetworkStatus('hidden')
  }, [])

  // 用户点击"重试"：重新触发连接尝试
  const handleRetry = useCallback(() => {
    console.log('[App] 用户点击重试')
    startConnecting()
  }, [startConnecting])

  // 检查token是否过期（只检查一次）
  useEffect(() => {
    if (!hasCheckedToken.current && expiresAt && Date.now() >= expiresAt && accessToken) {
      // Token已过期，清除认证状态
      useAuthStore.getState().logout()
      hasCheckedToken.current = true
    }
  }, [expiresAt, accessToken])

  // 同步本地数据库用户状态（数据隔离关键步骤）
  const lastUserIdRef = useRef<string | null>(null)
  
  useEffect(() => {
    const syncUser = async () => {
      // 未登录状态，重置为默认用户
      if (!isAuthenticated || !user?.id) {
        if (lastUserIdRef.current !== null) {
          try {
            await tauriApi.setCurrentUser('default-user')
            console.log('[App] 已重置为默认用户')
          } catch (error) {
            console.error('[App] 重置默认用户失败:', error)
          }
          lastUserIdRef.current = null
          setHasSyncedUser(false)
        }
        return
      }

      console.log('[App] 同步用户状态:', {
        from: lastUserIdRef.current,
        to: user.id
      })

      try {
        // 设置当前用户到本地数据库（仅在 Tauri 环境下有效）
        try {
          await tauriApi.setCurrentUser(user.id)
          console.log('[App] 本地数据库用户已同步:', user.id)
        } catch (tauriError) {
          // Web 环境下会失败，这是正常的
          console.log('[App] 非 Tauri 环境，跳过本地数据库用户设置')
        }

        // 更新最后同步的用户 ID
        lastUserIdRef.current = user.id
        // 无论用户ID是否变化，都设置 hasSyncedUser 为 true
        // 确保 Electric Sync 初始化不会被阻塞
        setHasSyncedUser(true)
        console.log('[App] hasSyncedUser 设置为 true')

      } catch (error) {
        console.error('[App] 同步本地数据库用户失败:', error)
        // 即使失败也设置 hasSyncedUser 为 true，让 Electric SQL 同步可以启动
        setHasSyncedUser(true)
      }
    }

    syncUser()
  }, [dispatch, isAuthenticated, user?.id])

  // 用户登录后自动加载笔记数据（已移至上面的 effect 中）
  // 保留此 effect 用于其他需要监听 isAuthenticated 的场景
  useEffect(() => {
    if (isAuthenticated && accessToken && hasSyncedUser) {
      // 数据已同步，可以执行其他操作
    }
  }, [isAuthenticated, accessToken, hasSyncedUser])

  // 检测忘记密码重置流程 - 登录后显示重置密码弹窗
  useEffect(() => {
    const checkForgetPasswordFlow = () => {
      const fromForgetPassword = localStorage.getItem('from_forget_password')

      console.log('[App] 检查忘记密码流程:', { fromForgetPassword, isAuthenticated, hasUser: !!user, hasSyncedUser, hasShown: hasShownResetModalRef.current })

      if (fromForgetPassword === 'true' && isAuthenticated && user && hasSyncedUser && !hasShownResetModalRef.current) {
        console.log('[App] 检测到忘记密码流程，显示重置密码弹窗')
        setShowResetModal(true)
        hasShownResetModalRef.current = true
        // 清除标记，避免重复显示
        localStorage.removeItem('from_forget_password')
      }
    }

    // 延迟检查，确保用户同步完成
    if (isAuthenticated && hasSyncedUser) {
      const timer = setTimeout(checkForgetPasswordFlow, 500)
      return () => clearTimeout(timer)
    }
  }, [isAuthenticated, user, hasSyncedUser])

  // 处理重置密码成功
  const handleResetSuccess = useCallback(() => {
    console.log('[App] 重置密码成功')
  }, [])

  // 处理重置密码弹窗关闭
  const handleResetClose = useCallback(() => {
    setShowResetModal(false)
  }, [])

  // Handle online/offline status
  // （离线/恢复的用户提示统一由 ReadonlyModeBanner 常驻横幅 + 自动只读模式呈现，
  //   此处仅维护 redux 在线状态供 UI 消费）
  useEffect(() => {
    const handleOnline = () => {
      dispatch(setOnlineStatus(true))
    }
    const handleOffline = () => {
      dispatch(setOnlineStatus(false))
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [dispatch])

  // Apply theme on mount and when theme changes
  useEffect(() => {
    dispatch(setTheme(theme))
  }, [dispatch, theme])

  useEffect(() => {
    if (!isMobile || mobileInitedRef.current) return
    mobileInitedRef.current = true

    setupMobileViewport()
    setupMobileSafeArea()
    // ★ 状态栏图标外观跟随应用主题（Tauri 移动端 LingShiTheme 桥；见 watchStatusBarAppearance 注释）
    unwatchStatusBarRef.current = watchStatusBarAppearance()
    localStorage.setItem('auto_suji_mode_startup', 'true')
    localStorage.setItem('last_editor_mode', 'suji')

    dispatch(setSidebarOpen(false))
    dispatch(setTocOutlineOpen(false))
    dispatch(setBlockReferencePanelOpen(false))
    dispatch(closeRightEditor())
    dispatch(closeAllNotesInRightEditor())
    dispatch(closeLeftEditorOnly())

    // 首次启动强制导航到速记模式（覆盖 StartupRedirect 的默认路由）
    // 排除 /login 和 /callback 路径，不干扰 OAuth 流程
    const currentPath = window.location.pathname
    if (!currentPath.startsWith('/suji') && !currentPath.startsWith('/login') && !currentPath.startsWith('/callback')) {
      navigate('/suji', { replace: true })
    }

    console.log('[App] 移动端初始化完成：默认速记模式，关闭所有侧边面板')
  }, [isMobile, dispatch, navigate])

  // 卸载时清理状态栏外观监听（watchStatusBarAppearance 的 MutationObserver）
  useEffect(() => {
    return () => {
      unwatchStatusBarRef.current?.()
    }
  }, [])

  // 登录完成后再次确保面板关闭（防止时序竞态）
  useEffect(() => {
    if (isAuthenticated && isMobile) {
      dispatch(setSidebarOpen(false))
      dispatch(setTocOutlineOpen(false))
      dispatch(setBlockReferencePanelOpen(false))
      dispatch(closeRightEditor())
      dispatch(closeLeftEditorOnly())
    }
  }, [isAuthenticated, isMobile, dispatch])

  // ★ Web 在线端：移动端浏览器打开时默认收起左侧边栏（桌面浏览器保持默认打开）
  const webMobileInitedRef = useRef(false)
  useEffect(() => {
    if (webMobileInitedRef.current) return
    if (!isWebMobileBrowser()) return
    webMobileInitedRef.current = true
    dispatch(setSidebarOpen(false))
    console.log('[App] Web 移动浏览器初始化完成：默认收起左侧边栏')
  }, [dispatch])

  return (
    <AppErrorBoundary>
    <EditorProvider>
      <ElectricSyncProvider userId={user?.id || null} enabled={isAuthenticated}>
        <SyncStatusProvider onRetry={handleRetry} onUseOffline={handleUseOffline}>
        <ElectricSyncStatusLogger />
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
          {/* 鸿蒙应用锁（B7）：开启后启动/回前台需生物识别解锁（未开启/非鸿蒙零渲染） */}
          <HarmonyAppLockGate />
          {/* ★ 网络异常统一降级形态（自动只读，见 useAutoReadonlyFallback）：
              横幅常驻四态提示（唯一提示面，不打断编辑） */}
          <ReadonlyModeBanner />
          {/* 慢启动提示（启动 >8s，全局单例，未触发时零渲染） */}
          <StartupSlowHintDialog />
          {/* 重置密码弹窗 - 忘记密码流程后显示 */}
          <ResetPasswordModal
            isOpen={showResetModal}
            onClose={handleResetClose}
            onSuccess={handleResetSuccess}
          />

          {/* 应用更新提示 Modal - 启动 5 秒后自动检查，有新版本时自动弹出 */}
          {appUpdateModalOpen && (
            <Suspense fallback={null}>
              <UpdateModal
                visible={appUpdateModalOpen}
                updateInfo={updateInfoForModal}
                isUpdating={isApplyingUpdate}
                isChecking={isUpdateChecking}
                progress={updateProgress}
                error={updateError}
                downloadStatus={updateDownloadStatus}
                onClose={() => {
                  // 仅关闭 Modal，不调用 dismissUpdate，保持版本可从用户菜单/设置页再次被检测到
                  setAppUpdateModalOpen(false)
                }}
                onUpdate={applyAppUpdate}
                onCheckNow={checkUpdateNow}
                onDismiss={() => {
                  // 用户明确点击"稍后提醒"时：标记该版本为忽略 + 关闭 Modal
                  dismissUpdate()
                  setAppUpdateModalOpen(false)
                }}
                onStartDownload={startUpdateDownload}
                onInstallDownloaded={installDownloadedUpdate}
                onRetryDownload={startUpdateDownload}
              />
            </Suspense>
          )}

        <Routes>
          {/* 登录页 */}
          <Route path="/login" element={<LoginPage />} />
          
          {/* Casdoor 回调页 */}
          <Route path="/callback" element={<CallbackPage />} />

          {/* 跨端登录设置（首次华为登录强制引导，不可跳过） */}
          <Route path="/bind-email" element={<EmailBindingPage />} />
          
          {/* 受保护的路由 - 需要登录 */}
          <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
            <Route index element={<StartupRedirect />} />
            {/* ★ 移动端 /notes 渲染 EditorModeHost（并入模式轨道）；桌面端 NotesPage */}
            <Route path="notes" element={<NotesRouteEntry />} />
            <Route path="notes-synced" element={<Suspense fallback={<RouteLoadingFallback />}><NotesPageWithSync /></Suspense>} />

            {/* 残通模式路由 */}
            <Route path="notes/:noteId" element={<Suspense fallback={<RouteLoadingFallback />}><MultiNoteEditorContainer /></Suspense>} />

            {/* 速记/CRDT 双模式保活宿主：5 条路由渲染同一元素类型（NotesRouteEntry）跨路由保活，
                模式切换不销毁编辑器（/notes 全部笔记页并入轨道，避免重挂载卡顿/会话误恢复） */}
            <Route path="suji" element={<NotesRouteEntry />} />
            <Route path="suji/:noteId" element={<NotesRouteEntry />} />
            <Route path="crdt/notes/:noteId" element={<NotesRouteEntry />} />
            <Route path="crdt/suji/:noteId" element={<NotesRouteEntry />} />
            
            <Route path="settings" element={<Suspense fallback={null}><SettingsPage /></Suspense>} />
            <Route path="plans" element={<Suspense fallback={null}><PlanSubscriptionPage /></Suspense>} />
            <Route path="tauri-test" element={<Suspense fallback={null}><TauriTestPage /></Suspense>} />
            <Route path="tiptap-test" element={<Suspense fallback={null}><TipTapMobileTestPage /></Suspense>} />

          </Route>

          <Route path="*" element={<Suspense fallback={null}><NotFoundPage /></Suspense>} />
        </Routes>
      </div>
        </SyncStatusProvider>
    </ElectricSyncProvider>
    </EditorProvider>
    </AppErrorBoundary>
  )
}

export default App
